/**
 * Holy Land Market — Global Quick View
 * -------------------------------------------------------------
 * Adds a clean "Quick View" button to every product card across the
 * whole site (homepage, collections, search, recommendations, etc.)
 * and opens the product in a modal without leaving the page.
 *
 * It reuses the theme's own product engine: the quick-view HTML is
 * fetched via ?view=quick-view and initialised by dispatching the
 * theme's `shopify:section:load` event, so variant pickers, image
 * gallery and add-to-cart all work exactly like the product page.
 *
 * Works on desktop AND mobile.
 */
(function () {
  'use strict';

  if (window.__hlQuickViewInit) { return; }
  window.__hlQuickViewInit = true;

  var MODAL_ID = 'hl-quick-view-modal';
  var LABEL = 'Quick View';
  var EYE =
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>' +
    '<circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.8"/></svg>';

  /* Card types across the theme. Add new {card, image} pairs here to
     extend quick view to future custom sections. */
  var CARD_TYPES = [
    { card: '.product-item', image: '.product-item__image-wrapper' }, // collections, search, featured, recommendations, recently viewed
    { card: '.hl-product-card', image: '.hl-product-card__image' },   // homepage best-sellers & tabbed collections
    { card: '.pg-card', image: '.pg-card__image-wrap' }               // custom product grid
  ];

  var restoreFocusEl = null;
  var currentSectionId = null;

  function cleanUrl(href) {
    if (!href) { return null; }
    return href.split('#')[0].split('?')[0];
  }

  function resolveProductUrl(card) {
    if (card.tagName === 'A') {
      var h = card.getAttribute('href') || '';
      if (h.indexOf('/products/') !== -1) { return cleanUrl(h); }
    }
    var a = card.querySelector('a[href*="/products/"]');
    return a ? cleanUrl(a.getAttribute('href')) : null;
  }

  function buildTrigger(url) {
    var t = document.createElement('span');
    t.className = 'hl-qv-trigger';
    t.setAttribute('role', 'button');
    t.setAttribute('tabindex', '0');
    t.setAttribute('data-product-url', url);
    t.setAttribute('aria-label', LABEL);
    t.innerHTML =
      '<span class="hl-qv-trigger__icon">' + EYE + '</span>' +
      '<span class="hl-qv-trigger__label">' + LABEL + '</span>';
    return t;
  }

  function injectTriggers(root) {
    root = root || document;
    for (var i = 0; i < CARD_TYPES.length; i++) {
      var type = CARD_TYPES[i];
      var cards = root.querySelectorAll(type.card);
      for (var j = 0; j < cards.length; j++) {
        var card = cards[j];
        if (card.__hlQv) { continue; }
        if (card.closest('#' + MODAL_ID)) { continue; } // never inside the modal itself
        var url = resolveProductUrl(card);
        if (!url) { continue; }
        card.__hlQv = true;
        var host = card.querySelector(type.image) || card;
        host.classList.add('hl-qv-host');
        host.appendChild(buildTrigger(url));
      }
    }
  }

  function getModal() { return document.getElementById(MODAL_ID); }

  function openModal() {
    var modal = getModal();
    if (!modal) { return; }
    restoreFocusEl = document.activeElement;
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('is-locked');
    var closeBtn = modal.querySelector('[data-hl-qv-close]');
    if (closeBtn) { try { closeBtn.focus(); } catch (e) {} }
  }

  function closeModal() {
    var modal = getModal();
    if (!modal || modal.getAttribute('aria-hidden') === 'true') { return; }
    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove('is-loading');
    document.documentElement.classList.remove('is-locked');

    // Let the theme clean up the product instance it created for us.
    if (currentSectionId) {
      document.dispatchEvent(new CustomEvent('shopify:section:unload', {
        detail: { sectionId: currentSectionId }
      }));
      currentSectionId = null;
    }

    var inner = modal.querySelector('.modal__inner');
    if (inner) { inner.innerHTML = ''; }

    if (restoreFocusEl && restoreFocusEl.focus) { try { restoreFocusEl.focus(); } catch (e) {} }
    restoreFocusEl = null;
  }

  function openQuickView(url) {
    var modal = getModal();
    if (!url) { return; }
    if (!modal) { window.location.href = url; return; } // graceful fallback

    var inner = modal.querySelector('.modal__inner');
    openModal();

    // If a previous product was still loaded, let the theme dispose it first.
    if (currentSectionId) {
      document.dispatchEvent(new CustomEvent('shopify:section:unload', {
        detail: { sectionId: currentSectionId }
      }));
      currentSectionId = null;
    }

    modal.classList.add('is-loading');
    inner.innerHTML = '';

    fetch(url + '?view=quick-view', { credentials: 'same-origin', method: 'GET' })
      .then(function (response) {
        if (!response.ok) { throw new Error('Request failed'); }
        return response.text();
      })
      .then(function (html) {
        if (modal.getAttribute('aria-hidden') === 'true') { return; } // closed while loading
        inner.innerHTML = html;
        modal.classList.remove('is-loading');

        // Boot the theme's product controller on the injected markup so
        // variants, gallery and add-to-cart behave like a real product page.
        var section = inner.querySelector('[data-section-id]');
        if (section) {
          currentSectionId = section.getAttribute('data-section-id');
          inner.dispatchEvent(new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { sectionId: currentSectionId }
          }));
        }
      })
      .catch(function () {
        modal.classList.remove('is-loading');
        inner.innerHTML =
          '<div class="hl-qv-error">Sorry, we couldn’t load this here. ' +
          '<a href="' + url + '">Open the product page</a>.</div>';
      });
  }

  /* -------- Open: intercept trigger clicks in capture phase so the
     surrounding card link never navigates away. -------- */
  document.addEventListener('click', function (e) {
    var trig = e.target.closest ? e.target.closest('.hl-qv-trigger') : null;
    if (!trig) { return; }
    e.preventDefault();
    e.stopPropagation();
    openQuickView(trig.getAttribute('data-product-url'));
  }, true);

  document.addEventListener('keydown', function (e) {
    var t = e.target;
    if (!t || !t.classList || !t.classList.contains('hl-qv-trigger')) { return; }
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      openQuickView(t.getAttribute('data-product-url'));
    }
  });

  /* -------- Close: X button, click on backdrop, Escape, and the
     theme's own modal:close event (fired after add-to-cart). -------- */
  document.addEventListener('click', function (e) {
    var modal = getModal();
    if (!modal || modal.getAttribute('aria-hidden') === 'true') { return; }
    if (e.target.closest('[data-hl-qv-close]')) { e.preventDefault(); closeModal(); return; }
    var dialog = modal.querySelector('.modal__dialog');
    if (modal.contains(e.target) && dialog && !dialog.contains(e.target)) { closeModal(); }
  });

  document.addEventListener('keyup', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') { closeModal(); }
  });

  document.addEventListener('modal:close', closeModal);

  /* -------- Keep triggers present as the DOM changes (collection
     filtering, homepage tab switches, infinite scroll, etc.) -------- */
  function boot() { injectTriggers(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.closest && e.target.closest('#' + MODAL_ID)) { return; }
    setTimeout(function () { injectTriggers(document); }, 50);
  });

  if (window.MutationObserver) {
    var queued = false;
    var observer = new MutationObserver(function () {
      if (queued) { return; }
      queued = true;
      setTimeout(function () { queued = false; injectTriggers(document); }, 250);
    });
    var start = function () { if (document.body) { observer.observe(document.body, { childList: true, subtree: true }); } };
    if (document.body) { start(); } else { document.addEventListener('DOMContentLoaded', start); }
  }
})();